import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Modal, Form, Tab, Tabs, Row, Col, ListGroup } from 'react-bootstrap';
import surveyService from '../services/surveyService';

const SurveyManager = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    active: true,
    anonymous: false,
    allowMultipleSubmissions: false,
    questionsPerPage: 5,
    startDate: '',
    endDate: ''
  });

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    questionText: '',
    questionType: 'MULTIPLE_CHOICE',
    required: true,
    orderIndex: 0,
    options: []
  });
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);

  const questionTypes = [
    { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice' },
    { value: 'SINGLE_CHOICE', label: 'Single Choice (Radio)' },
    { value: 'TEXT', label: 'Text Input' },
    { value: 'TEXTAREA', label: 'Long Text' },
    { value: 'NUMBER', label: 'Number' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'DATE', label: 'Date' },
    { value: 'TIME', label: 'Time' },
    { value: 'DATETIME', label: 'Date & Time' },
    { value: 'RATING', label: 'Rating Scale' },
    { value: 'YES_NO', label: 'Yes/No' },
    { value: 'FILE_UPLOAD', label: 'File Upload' }
  ];

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await surveyService.getAllSurveys();
      setSurveys(response.data.content || response.data);
    } catch (error) {
      console.error('Error loading surveys:', error);
      setError('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async (surveyId) => {
    try {
      const response = await surveyService.getSurveyStatistics(surveyId);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setError('Failed to load survey statistics');
    }
  };

  const handleShowModal = async (survey = null) => {
    if (survey) {
      setEditingSurvey(survey);
      setFormData({
        title: survey.title,
        description: survey.description || '',
        active: survey.active,
        anonymous: survey.anonymous,
        allowMultipleSubmissions: survey.allowMultipleSubmissions,
        questionsPerPage: survey.questionsPerPage,
        startDate: survey.startDate ? survey.startDate.split('T')[0] : '',
        endDate: survey.endDate ? survey.endDate.split('T')[0] : ''
      });
      
      // Load existing questions
      try {
        const response = await surveyService.getSurveyById(survey.id);
        setQuestions(response.data.questions || []);
      } catch (error) {
        console.error('Error loading survey questions:', error);
        setQuestions([]);
      }
    } else {
      setEditingSurvey(null);
      setQuestions([]);
      setFormData({
        title: '',
        description: '',
        active: true,
        anonymous: false,
        allowMultipleSubmissions: false,
        questionsPerPage: 5,
        startDate: '',
        endDate: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSurvey(null);
    setQuestions([]);
    setError('');
    setSuccess('');
  };

  const handleShowQuestionModal = (questionIndex = null) => {
    if (questionIndex !== null) {
      setEditingQuestionIndex(questionIndex);
      const question = questions[questionIndex];
      setCurrentQuestion({
        questionText: question.questionText,
        questionType: question.questionType,
        required: question.required,
        orderIndex: question.orderIndex,
        options: question.options || []
      });
    } else {
      setEditingQuestionIndex(null);
      setCurrentQuestion({
        questionText: '',
        questionType: 'MULTIPLE_CHOICE',
        required: true,
        orderIndex: questions.length,
        options: []
      });
    }
    setShowQuestionModal(true);
  };

  const handleCloseQuestionModal = () => {
    setShowQuestionModal(false);
    setEditingQuestionIndex(null);
    setCurrentQuestion({
      questionText: '',
      questionType: 'MULTIPLE_CHOICE',
      required: true,
      orderIndex: 0,
      options: []
    });
  };

  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentQuestion(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { optionText: '', orderIndex: prev.options.length }]
    }));
  };

  const updateOption = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, optionText: value } : option
      )
    }));
  };

  const removeOption = (index) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const saveQuestion = () => {
    if (!currentQuestion.questionText.trim()) {
      setError('Question text is required');
      return;
    }

    const needsOptions = ['MULTIPLE_CHOICE', 'SINGLE_CHOICE', 'RATING'].includes(currentQuestion.questionType);
    if (needsOptions && currentQuestion.options.length === 0) {
      setError('This question type requires at least one option');
      return;
    }

    const newQuestions = [...questions];
    if (editingQuestionIndex !== null) {
      newQuestions[editingQuestionIndex] = currentQuestion;
    } else {
      newQuestions.push(currentQuestion);
    }

    setQuestions(newQuestions);
    handleCloseQuestionModal();
    setError('');
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < questions.length) {
      [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
      // Update order indices
      newQuestions.forEach((q, i) => q.orderIndex = i);
      setQuestions(newQuestions);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (questions.length === 0) {
      setError('Survey must have at least one question');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      
      const surveyData = {
        ...formData,
        startDate: formData.startDate ? `${formData.startDate}T00:00:00` : null,
        endDate: formData.endDate ? `${formData.endDate}T23:59:59` : null,
        questions: questions.map((q, index) => ({
          ...q,
          orderIndex: index
        }))
      };

      if (editingSurvey) {
        await surveyService.updateSurvey(editingSurvey.id, surveyData);
        setSuccess('Survey updated successfully!');
      } else {
        await surveyService.createSurvey(surveyData);
        setSuccess('Survey created successfully!');
      }
      
      loadSurveys();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.error('Error saving survey:', error);
      setError('Failed to save survey');
    }
  };

  const handleDelete = async (surveyId) => {
    if (window.confirm('Are you sure you want to delete this survey? This action cannot be undone.')) {
      try {
        await surveyService.deleteSurvey(surveyId);
        setSuccess('Survey deleted successfully!');
        loadSurveys();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting survey:', error);
        setError('Failed to delete survey');
      }
    }
  };

  const handleViewStatistics = (survey) => {
    setSelectedSurvey(survey);
    loadStatistics(survey.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getSurveyStatus = (survey) => {
    if (!survey.active) return { variant: 'secondary', text: 'Inactive' };
    
    const now = new Date();
    const startDate = survey.startDate ? new Date(survey.startDate) : null;
    const endDate = survey.endDate ? new Date(survey.endDate) : null;

    if (startDate && now < startDate) {
      return { variant: 'info', text: 'Upcoming' };
    }
    if (endDate && now > endDate) {
      return { variant: 'warning', text: 'Expired' };
    }
    return { variant: 'success', text: 'Active' };
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading surveys...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Survey Management</h4>
        <Button variant="primary" onClick={() => handleShowModal()}>
          <i className="fas fa-plus me-2"></i>
          Create Survey
        </Button>
      </div>

      {/* Alerts */}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Surveys Table */}
      <Card>
        <Card.Body className="p-0">
          {surveys.length === 0 ? (
            <div className="text-center p-4">
              <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
              <p className="text-muted">No surveys created yet</p>
            </div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th className="d-none d-md-table-cell">Questions</th>
                  <th className="d-none d-lg-table-cell">Responses</th>
                  <th className="d-none d-lg-table-cell">Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map(survey => {
                  const status = getSurveyStatus(survey);
                  return (
                    <tr key={survey.id}>
                      <td>
                        <div>
                          <strong>{survey.title}</strong>
                          {survey.description && (
                            <div className="text-muted small">
                              {survey.description.length > 50 
                                ? `${survey.description.substring(0, 50)}...`
                                : survey.description
                              }
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <div>
                            <Badge bg={status.variant}>{status.text}</Badge>
                            {survey.anonymous && (
                              <Badge bg="info" className="ms-1">Anonymous</Badge>
                            )}
                          </div>
                          <div className="d-md-none mt-1">
                            <small className="text-muted">
                              {survey.totalQuestions || 0} questions • {formatDate(survey.createdAt)}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-md-table-cell">{survey.totalQuestions || 0}</td>
                      <td className="d-none d-lg-table-cell">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleViewStatistics(survey)}
                          className="p-0"
                        >
                          View Responses
                        </Button>
                      </td>
                      <td className="d-none d-lg-table-cell">{formatDate(survey.createdAt)}</td>
                      <td>
                        <div className="btn-group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(survey)}
                            title="Edit Survey"
                          >
                            <i className="fas fa-edit"></i>
                            <span className="d-lg-none ms-1">Edit</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(survey.id)}
                            title="Delete Survey"
                          >
                            <i className="fas fa-trash"></i>
                            <span className="d-lg-none ms-1">Delete</span>
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewStatistics(survey)}
                            className="d-lg-none"
                            title="View Statistics"
                          >
                            <i className="fas fa-chart-bar"></i>
                            <span className="ms-1">Stats</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Survey Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" className="mobile-friendly-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingSurvey ? 'Edit Survey' : 'Create New Survey'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Tabs defaultActiveKey="basic" className="mb-3">
              <Tab eventKey="basic" title="Basic Info">
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Questions Per Page</Form.Label>
                  <Form.Control
                    type="number"
                    name="questionsPerPage"
                    value={formData.questionsPerPage}
                    onChange={handleChange}
                    min="1"
                    max="20"
                  />
                </Form.Group>

                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="active"
                    label="Active (visible to users)"
                    checked={formData.active}
                    onChange={handleChange}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    name="anonymous"
                    label="Anonymous (no login required)"
                    checked={formData.anonymous}
                    onChange={handleChange}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    name="allowMultipleSubmissions"
                    label="Allow multiple submissions per user"
                    checked={formData.allowMultipleSubmissions}
                    onChange={handleChange}
                  />
                </div>
              </Tab>

              <Tab eventKey="questions" title={`Questions (${questions.length})`}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>Survey Questions</h6>
                  <Button variant="success" size="sm" onClick={() => handleShowQuestionModal()}>
                    <i className="fas fa-plus me-1"></i>
                    Add Question
                  </Button>
                </div>

                {questions.length === 0 ? (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    No questions added yet. Click "Add Question" to get started.
                  </Alert>
                ) : (
                  <ListGroup>
                    {questions.map((question, index) => (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <h6 className="mb-1">
                              {index + 1}. {question.questionText}
                            </h6>
                            <div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleShowQuestionModal(index)}
                                className="me-1"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => moveQuestion(index, 'up')}
                                disabled={index === 0}
                                className="me-1"
                              >
                                <i className="fas fa-arrow-up"></i>
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => moveQuestion(index, 'down')}
                                disabled={index === questions.length - 1}
                                className="me-1"
                              >
                                <i className="fas fa-arrow-down"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeQuestion(index)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </div>
                          <p className="mb-1">
                            <Badge bg="secondary" className="me-2">
                              {questionTypes.find(t => t.value === question.questionType)?.label}
                            </Badge>
                            {question.required && <Badge bg="warning">Required</Badge>}
                          </p>
                          {question.options && question.options.length > 0 && (
                            <small className="text-muted">
                              Options: {question.options.map(opt => opt.optionText).join(', ')}
                            </small>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={questions.length === 0}>
              {editingSurvey ? 'Update Survey' : 'Create Survey'}
              {questions.length === 0 && ' (Add questions first)'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Question Modal */}
      <Modal show={showQuestionModal} onHide={handleCloseQuestionModal} size="lg" className="mobile-friendly-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Question Text *</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="questionText"
              value={currentQuestion.questionText}
              onChange={handleQuestionChange}
              placeholder="Enter your question here..."
            />
          </Form.Group>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Question Type</Form.Label>
                <Form.Select
                  name="questionType"
                  value={currentQuestion.questionType}
                  onChange={handleQuestionChange}
                >
                  {questionTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>&nbsp;</Form.Label>
                <Form.Check
                  type="checkbox"
                  name="required"
                  label="Required"
                  checked={currentQuestion.required}
                  onChange={handleQuestionChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {['MULTIPLE_CHOICE', 'SINGLE_CHOICE', 'RATING'].includes(currentQuestion.questionType) && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>Options</Form.Label>
                <Button variant="outline-success" size="sm" onClick={addOption}>
                  <i className="fas fa-plus me-1"></i>
                  Add Option
                </Button>
              </div>
              
              {currentQuestion.options.length === 0 ? (
                <Alert variant="warning" className="small">
                  This question type requires at least one option.
                </Alert>
              ) : (
                currentQuestion.options.map((option, index) => (
                  <div key={index} className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={option.optionText}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-2"
                      onClick={() => removeOption(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseQuestionModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveQuestion}>
            {editingQuestionIndex !== null ? 'Update Question' : 'Add Question'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Statistics Modal */}
      <Modal 
        show={selectedSurvey !== null} 
        onHide={() => setSelectedSurvey(null)} 
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Survey Statistics: {selectedSurvey?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statistics ? (
            <Tabs defaultActiveKey="overview">
              <Tab eventKey="overview" title="Overview">
                <div className="p-3">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="text-center">
                        <h3 className="text-primary">{statistics.totalResponses}</h3>
                        <p className="text-muted">Total Responses</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h3 className="text-success">{statistics.survey.totalQuestions || 0}</h3>
                        <p className="text-muted">Questions</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h3 className="text-info">
                          {statistics.responses.filter(r => r.completionTime).length > 0
                            ? Math.round(
                                statistics.responses
                                  .filter(r => r.completionTime)
                                  .reduce((sum, r) => sum + r.completionTime, 0) /
                                statistics.responses.filter(r => r.completionTime).length / 60
                              )
                            : 0
                          }m
                        </h3>
                        <p className="text-muted">Avg. Completion Time</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h3 className="text-warning">
                          {Math.round((statistics.totalResponses / Math.max(statistics.totalResponses, 1)) * 100)}%
                        </h3>
                        <p className="text-muted">Completion Rate</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="responses" title="Responses">
                <div className="p-3">
                  {statistics.responses.length === 0 ? (
                    <p className="text-muted text-center">No responses yet</p>
                  ) : (
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Started</th>
                          <th>Completed</th>
                          <th>Duration</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statistics.responses.map(response => (
                          <tr key={response.id}>
                            <td>
                              {response.user ? response.user.username : 'Anonymous'}
                            </td>
                            <td>
                              {new Date(response.startedAt).toLocaleString()}
                            </td>
                            <td>
                              {response.completedAt 
                                ? new Date(response.completedAt).toLocaleString()
                                : 'In Progress'
                              }
                            </td>
                            <td>
                              {response.completionTime 
                                ? `${Math.round(response.completionTime / 60)}m ${response.completionTime % 60}s`
                                : '-'
                              }
                            </td>
                            <td>{response.ipAddress || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Tab>
            </Tabs>
          ) : (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading statistics...</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SurveyManager;